import { useState } from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import {
  ArrowUturnDownIcon,
  CheckIcon,
  FunnelIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import { Card } from '../../../components/base/Card';
import { Tag } from '../../../components/base/Tag';
import { DataTable } from '../../../components/base/Table';
import { Button } from '../../../components/base/Button';
import { useNotification } from '../../../hooks/useNotifications';
import { ConfirmDelete } from '../../../components/shared/ConfirmDelete';
import { useGlobalHardwareModelFilter } from '../../../contexts/GlobalHardwareFilterContext';
import { GlobalHardwareModelFilterBanner } from '../../../components/shared/GlobalHardwareModelFilterBanner';
import { LoadingWheel } from '../../../components/base/LoadingWheel';
import { useSuperAdminApplianceLookups } from '../../../hooks/useSuperAdminHardwareLookups';
import { useClipboardCopy } from '../../../hooks/useClipboardCopy';
import {
  ApplianceModelClassification,
  ApplianceModelClassificationColours,
  IApplianceModel,
} from '../../../types/Appliance/ApplianceModel';
import useApplianceModelsMutations from '../../../data/appliance/useApplianceModelMutations';
import { IApplianceManufacturer } from '../../../types/Appliance/ApplianceManufacturer';
import { applianceQueryKeys } from '../../../data/QueryKeys';
import { CreateEditApplianceModel } from '../../../components/Appliance/CreateEditApplianceModel';

const colHelper = createColumnHelper<IApplianceModel>();

export function ApplianceModelsPage() {
  const { CopyToClipboardButton } = useClipboardCopy();
  const addNotification = useNotification();
  const { delApplianceModel, updateApplianceModel } =
    useApplianceModelsMutations();
  const { setGlobalHardwareModelFilter } = useGlobalHardwareModelFilter();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editApplianceModel, setEditApplianceModel] = useState<
    IApplianceModel | undefined
  >(undefined);
  const [deleteApplianceModel, setDeleteApplianceModel] = useState<
    IApplianceModel | undefined
  >(undefined);
  //TODO: Possibly combine the below?
  const { isLoading: isTypeLoading, typeLookup } =
    useSuperAdminApplianceLookups();
  const { isLoading: isHardwareLoading, manufacturerLookup } =
    useSuperAdminApplianceLookups();

  const handleDeleteApplianceModel = (applianceModel: IApplianceModel) => {
    setDeleteApplianceModel(applianceModel);
    setDeleteOpen(true);
  };

  const handleConfirmDeleteApplianceModel = () => {
    delApplianceModel.mutate(deleteApplianceModel!);
    setDeleteOpen(false);
  };

  const handleRestoreApplianceModel = (applianceModel: IApplianceModel) => {
    updateApplianceModel.mutate({ ...applianceModel, restore: true });
  };

  const handleEditApplianceModel = (applianceModel: IApplianceModel) => {
    setEditApplianceModel(applianceModel);
    setEditOpen(true);
  };

  const handleCreateEditClose = () => {
    editApplianceModel &&
      setTimeout(() => setEditApplianceModel(undefined), 200);
    setEditOpen(false);
  };

  const handleSetModelFilter = (applianceModel: IApplianceModel) => {
    applianceModel.hardwareManufacturer = manufacturerLookup(
      applianceModel.hardwareManufacturerId
    ) as IApplianceManufacturer;
    setGlobalHardwareModelFilter({ type: 'set', payload: applianceModel });
    addNotification(
      'success',
      `Global Filter set to ${applianceModel.hardwareManufacturer.name} ${applianceModel.name}`
    );
  };

  const cols = [
    colHelper.display({
      header: 'Status',
      cell: (info) =>
        info.row.original.disabled ? (
          <XMarkIcon className="w-5 h-5 opacity-70" />
        ) : (
          <CheckIcon className="w-5 h-5 opacity-70" />
        ),
    }),
    colHelper.accessor((row) => typeLookup(row.hardwareTypeId)?.name, {
      header: 'Type',
    }),
    colHelper.accessor(
      (row) => manufacturerLookup(row.hardwareManufacturerId)?.name,
      { header: 'Manufacturer' }
    ),
    colHelper.accessor('name', { header: 'Model' }),
    colHelper.display({
      header: 'Classification',
      cell: (info) => (
        <>
          {info.row.original.classification !== 0 ? (
            <Tag
              className="min-w-[80px]"
              label={
                ApplianceModelClassification[info.row.original.classification]
              }
              colour={
                ApplianceModelClassificationColours[
                  info.row.original.classification
                ]
              }
            />
          ) : (
            ''
          )}
          {info.row.original.partial ? (
            <Tag className="min-w-[80px]" label="Partial" colour="C6C6C6" />
          ) : (
            ''
          )}
        </>
      ),
    }),
    colHelper.display({
      header: 'Actions',
      cell: (info) => (
        <div className="flex items-center space-x-4">
          {info.cell.row.original.deletedOn ? (
            <ArrowUturnDownIcon
              className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
              onClick={() => handleRestoreApplianceModel(info.row.original)}
            />
          ) : (
            <>
              <CopyToClipboardButton iconOnly copyText={info.row.original.id} />
              <PencilIcon
                className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
                onClick={() => handleEditApplianceModel(info.row.original)}
              />
              <TrashIcon
                className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
                onClick={() => handleDeleteApplianceModel(info.row.original)}
              />
              <FunnelIcon
                className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
                onClick={() => handleSetModelFilter(info.row.original)}
                title={`Set global hardware filter to ${info.row.original.name}`}
              />
            </>
          )}
        </div>
      ),
    }),
  ];

  return (
    <>
      <CreateEditApplianceModel
        open={editOpen}
        onClose={handleCreateEditClose}
        hardwareModel={editApplianceModel}
      />
      <ConfirmDelete
        itemName={deleteApplianceModel?.name}
        open={deleteOpen}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={() => handleConfirmDeleteApplianceModel()}
      />
      <Card title="Appliance Models">
        <div className="my-6">
          <GlobalHardwareModelFilterBanner />
        </div>
        <div className="my-6">
          <Button onClick={() => setEditOpen(true)}>Add Model</Button>
        </div>
        <div>
          {isTypeLoading || isHardwareLoading ? (
            <LoadingWheel />
          ) : (
            <DataTable<IApplianceModel>
              url="/superadmin/hardware/models"
              queryKey={applianceQueryKeys.superAdminModels()}
              columns={cols}
              enableShowDeleted
            />
          )}
        </div>
      </Card>
    </>
  );
}
